import clsx from 'clsx';
import { Tooltip } from '@/components/Tooltip/Tooltip';
import { isTrue } from '@/utils/is';
import './List.scss';

type ListHeadingProps = Readonly<{ label: string; action?: React.ReactNode }>;

function ListHeading({ label, action }: ListHeadingProps) {
	return (
		<div className="d-flex justify-between items-start gap-lg">
			<h2 className="flex-1 text-list-heading color-text-primary">{label}</h2>
			{action}
		</div>
	);
}

type ListItemLabelProps = Readonly<{ children: React.ReactNode }>;

function ListItemLabel({ children }: ListItemLabelProps) {
	return <dt className="flex-1 text-list-item color-text-secondary">{children}</dt>;
}

type ListItemValueProps = Readonly<{ children: React.ReactNode }>;

function ListItemValue({ children }: ListItemValueProps) {
	return <dd className="d-flex items-center gap-3xs text-list-item color-text-secondary text-end">{children}</dd>;
}

type ListItemProps = Readonly<{
	id?: string;
	label: React.ReactNode;
	labelTooltip?: string;
	value?: React.ReactNode;
	valueTooltip?: string;
	// The variant 'stacked` is used when the label and value should be on separate lines, otherwise they will be on the same line with space between them.
	// The `stacked` variant is typically used for longer text that would not fit on the same line an the value is wrapped by default in this scenario.
	variant?: 'default' | 'stacked';
}>;

export type ListItems = Array<ListItemProps>;

function ListItem({ label, labelTooltip, value, valueTooltip, variant = 'default' }: ListItemProps) {
	return (
		<div className="[ list-item ] [ d-flex justify-between ]" data-variant={variant}>
			<ListItemLabel>
				{label}
				{labelTooltip && (
					<Tooltip
						tooltipTriggerAsChild
						tooltipTriggerChild={
							<button className="[ p-0 ml-2xs border-none bg-none ] [ d-inline-flex items-center ]" type="button">
								<svg className="icon list-item__tooltip d-block" width="1em" height="1em" fill="currentColor">
									<use xlinkHref="/cars/bz4x/build/speedboat/icons/toyota-icons.svg#info-circle" />
								</svg>
							</button>
						}
					>
						<p dangerouslySetInnerHTML={{ __html: labelTooltip || '' }} />
					</Tooltip>
				)}
			</ListItemLabel>
			{value && (
				<ListItemValue>
					{isTrue(value) ? (
						<svg className="[ icon ]" width="1em" height="1em" fill="currentColor">
							<use xlinkHref="/cars/bz4x/build/speedboat/icons/toyota-icons.svg#check-lg" />
						</svg>
					) : (
						value
					)}
					{valueTooltip && (
						<Tooltip
							tooltipTriggerAsChild
							tooltipTriggerChild={
								<button className="[ list-item__tooltip-trigger ] [ p-0 ml-2xs border-none bg-none ]" type="button">
									<svg className="icon" width="1em" height="1em" fill="currentColor">
										<use xlinkHref="/cars/bz4x/build/speedboat/icons/toyota-icons.svg#info-circle" />
									</svg>
								</button>
							}
						>
							<p dangerouslySetInnerHTML={{ __html: valueTooltip || '' }} />
						</Tooltip>
					)}
				</ListItemValue>
			)}
		</div>
	);
}

export type ListProps = {
	className?: string;
	description?: string;
	heading?: ListHeadingProps;
	listItems: ListItems;
	subheading?: string;
	variant?: 'default' | 'blocks' | 'stacked';
};

export function List({ className, description, heading, listItems, subheading, variant = 'default' }: ListProps) {
	const hasHeader = heading || subheading || description;
	const hasList = listItems && listItems.length > 0;

	return (
		(hasHeader || hasList) && (
			<div className={clsx('list', className)}>
				{hasHeader && (
					<div className="mb-sm d-flex d-flex-column gap-2xs" data-tesid="list-header">
						{heading && <ListHeading {...heading} />}
						{subheading && (
							<h3 className="text-list-subheading color-text-primary" data-tesid="subHeading">
								{subheading}{' '}
							</h3>
						)}
						{description && (
							<p className="text-body-base color-text-secondary" data-tesid="description">
								{description}
							</p>
						)}
					</div>
				)}
				{hasList && (
					<dl className="m-0 p-0" data-variant={variant} data-tesid="data">
						{listItems.map(({ id, ...listItem }, index) => (
							<ListItem key={id || index} variant={variant === 'stacked' ? 'stacked' : 'default'} {...listItem} />
						))}
					</dl>
				)}
			</div>
		)
	);
}
